<template>
  <div class="drawer" :class="{ 'is-active': isOpen }">
    <div
      class="drawer__backdrop"
      :style="`z-index: ${backdropZIndex}`"
      @click="handleClose"
    />

    <div
      class="drawer__content"
      :style="`z-index: ${contentZIndex}; right: ${stackOffset}px; ${maxWidth ? `max-width: ${maxWidth};` : 'max-width: 1164px'}`"
    >
      <div v-if="title" class="drawer__header">
        <div class="tw-flex tw-items-center tw-gap-4">
          <c-button-icon
            v-if="showBackButton"
            outline
            :icon="toClassicIcon(chevronLeftIcon, 'solid')"
            rounded
            class="tw-aspect-square"
            @click="handleBack"
          />

          <div class="tw-flex tw-items-center tw-gap-2">
            <q-icon
              v-if="titleIcon"
              :name="titleIcon"
              size="16px"
              class="tw-text-grey-300"
            />

            <h4>{{ title }}</h4>
          </div>
        </div>

        <!-- No gap: the header buttons are 40x40 icon boxes that read as one
             group, so they sit flush against each other. -->
        <div class="tw-flex tw-items-center">
          <slot name="header-actions" />

          <c-button-icon
            flat
            color="grey-900"
            :icon="toClassicIcon(closeIcon)"
            :aria-label="closeLabel"
            :tooltip="closeLabel"
            class="drawer__close"
            @click="handleClose"
          />
        </div>
      </div>

      <div class="drawer__body">
        <div class="drawer__slot">
          <slot />
        </div>

        <div v-if="$slots.actions" class="drawer__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { chevronLeftIcon, closeIcon, toClassicIcon } from "common/defaultIcons";
import { HEADER_HEIGHT_PX } from "common/UIConstants";
import {
  CLOSE_ANIMATION_DURATION,
  CLOSE_ANIMATION_DURATION_IN_MS,
  acquireDrawerDepth,
  releaseDrawerDepth,
  closingSidebars,
} from "shared/components/wrappers/drawer/drawerHelper";
import {
  deserializeStack,
  getParamKeysForSidebar,
} from "shared/composables/useDrawerStack";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const ANIMATION_DELAY_TIME_IN_MS = 10;

// --- Z-index system ---
// Each drawer layer needs 2 z-index slots: backdrop + content.
// Base starts at 100, with a gap of 10 per layer for breathing room.
const BASE_Z = 100;
const Z_LAYER_GAP = 10;

// Router
const router = useRouter();
const route = useRoute();

// Emits
const emit = defineEmits(["close"]);

// Props
const props = defineProps({
  maxWidth: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  // Optional icon left of the title, e.g. `toClassicIcon(fileIcon)`.
  titleIcon: {
    type: String,
    default: null,
  },
  // The close button is a bare glyph, so this is the only name a screen reader
  // gets for it.
  closeLabel: {
    type: String,
    default: "Sluiten",
  },
  name: {
    type: String,
    default: null,
  },
  modelId: {
    type: [String, Number],
    required: false,
    default: null,
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  closeOnRouteChange: {
    type: Boolean,
    default: false,
  },
  ownsSidebar: {
    type: Boolean,
    default: false,
  },
  sidebarName: {
    type: String,
    default: null,
  },
});

// Legacy depth counter — only used for non-sidebar drawers (e.g. FilterDrawer)
// that don't participate in the URL stack system.
const legacyDepth = acquireDrawerDepth();

// Refs
const isOpen = ref(false);
const isClosing = ref(false);

// The sidebar value this drawer instance was opened with.
const sidebarValue = ref(null);

// Resolve the sidebar identity immediately from props when available.
// This is set BEFORE onMounted so that computed z-index works from first render.
if (props.sidebarName) {
  sidebarValue.value = props.sidebarName;
} else if (props.name) {
  sidebarValue.value = props.name;
}

// --- Stack-aware computed ---

/**
 * Parse the current sidebar stack from the URL.
 */
const currentStack = computed(() => deserializeStack(route.query));

/**
 * Returns the index of this drawer in the stack (-1 if not found).
 * Uses sidebarValue which is set from props immediately in setup.
 */
const stackIndex = computed(() => {
  if (!sidebarValue.value) {
    return -1;
  }

  return currentStack.value.findIndex(
    (entry) => entry.sidebar === sidebarValue.value,
  );
});

/**
 * The effective depth for z-index calculation.
 * - Sidebar drawers: derived from URL stack position (always correct, reactive).
 * - Non-sidebar drawers (FilterDrawer etc.): use legacy counter.
 */
const effectiveDepth = computed(() => {
  if (sidebarValue.value && stackIndex.value >= 0) {
    return stackIndex.value;
  }

  // Non-sidebar drawer: use legacy counter, but offset above any sidebar stack
  return currentStack.value.length + legacyDepth;
});

/**
 * Reactive z-index values derived from stack position.
 * Backdrop is always 1 below content so it sits behind the drawer panel
 * but above any drawer layers below this one.
 */
const contentZIndex = computed(
  () => BASE_Z + effectiveDepth.value * Z_LAYER_GAP + 1,
);
const backdropZIndex = computed(
  () => BASE_Z + effectiveDepth.value * Z_LAYER_GAP,
);

/**
 * Show back button when this drawer is stacked on top of another.
 */
const showBackButton = computed(() => stackIndex.value > 0);

/**
 * Visual stack offset: drawers that have other drawers on top of them
 * shift left to peek out, creating a layered card effect.
 * The topmost drawer stays flush right. Each drawer below shifts further left.
 *
 * Responsive:
 * - Mobile (< 768px): no offset, back button is the only stack indicator
 * - Tablet (768–1279px): 16px per layer
 * - Desktop (≥ 1280px): 24px per layer
 */
const BREAKPOINT_MD = 768;
const BREAKPOINT_XL = 1280;

const drawersAbove = computed(() => {
  const stack = currentStack.value;

  if (stackIndex.value < 0 || stack.length <= 1) {
    return 0;
  }

  // Count drawers above this one, excluding any that are currently closing.
  // This lets the offset animation start immediately when a drawer above
  // begins its close animation, instead of waiting for the URL to update.
  const above = stack.slice(stackIndex.value + 1);

  return above.filter((entry) => !closingSidebars.has(entry.sidebar)).length;
});

const stackOffset = computed(() => {
  if (drawersAbove.value === 0) {
    return 0;
  }

  const width = window.innerWidth;

  if (width < BREAKPOINT_MD) {
    return 0;
  }

  const offsetPerLayer = width >= BREAKPOINT_XL ? 24 : 16;

  return drawersAbove.value * offsetPerLayer;
});

// --- Methods ---

/**
 * Removes this drawer (and any above it) from the sidebar stack in the URL.
 * Only removes this drawer's own params, preserving params from drawers below.
 */
const removeSidebarFromStack = async () => {
  if (!sidebarValue.value) {
    return;
  }

  const stack = currentStack.value;
  const index = stack.findIndex(
    (entry) => entry.sidebar === sidebarValue.value,
  );

  // If not found in stack, nothing to clean up
  if (index === -1) {
    return;
  }

  // Keep everything below this drawer in the stack
  const newStack = stack.slice(0, index);

  // Collect param keys to remove (this drawer + all above it)
  const removedEntries = stack.slice(index);
  const keysToRemove = new Set();

  for (const entry of removedEntries) {
    for (const key of getParamKeysForSidebar(entry.sidebar)) {
      keysToRemove.add(key);
    }
  }

  // Build new query: keep non-sidebar params and params from remaining stack entries
  const newQuery = {};

  // Preserve non-sidebar query params
  for (const [key, value] of Object.entries(route.query)) {
    if (key === "sidebar" || keysToRemove.has(key)) {
      continue;
    }

    newQuery[key] = value;
  }

  // Set new sidebar value (comma-separated remaining entries, or remove entirely)
  if (newStack.length > 0) {
    newQuery.sidebar = newStack.map((entry) => entry.sidebar).join(",");

    // Re-add params from remaining stack entries
    for (const entry of newStack) {
      for (const [key, value] of Object.entries(entry.params)) {
        newQuery[key] = value;
      }
    }
  }

  await router.push({ query: newQuery });
};

const close = async () => {
  if (isClosing.value) {
    return;
  }

  isClosing.value = true;
  isOpen.value = false;

  // Signal immediately so drawers below start their offset animation
  // in parallel with this drawer's close animation.
  if (sidebarValue.value) {
    closingSidebars.add(sidebarValue.value);
  }

  await new Promise((resolve) => setTimeout(resolve, CLOSE_ANIMATION_DURATION));

  if (sidebarValue.value) {
    closingSidebars.delete(sidebarValue.value);
    await removeSidebarFromStack();
  }

  if (!props.persistent) {
    emit("close");
  }
};

/**
 * Handles the back button: pops this drawer from the stack, revealing the one below.
 */
const handleBack = async () => {
  await close();
};

const handleClose = async () => {
  if (!props.persistent) {
    close();
  } else {
    return new Promise((resolve, reject) => {
      emit("close", {
        resolve: async () => {
          await close();
          resolve();
        },
        reject,
      });
    });
  }
};

defineExpose({ close });

const checkForEscapePress = (event) => {
  if (event.key === "Escape") {
    // Only handle ESC on the topmost drawer in the stack
    const stack = currentStack.value;

    if (stack.length > 0 && sidebarValue.value) {
      const topmostSidebar = stack[stack.length - 1]?.sidebar;

      if (topmostSidebar !== sidebarValue.value) {
        return; // Not the topmost — ignore ESC
      }
    }

    handleClose();
  }
};

onBeforeUnmount(() => {
  releaseDrawerDepth();
  document.body.classList.remove("overflow-hidden");
  window.removeEventListener("keydown", checkForEscapePress);
});

onMounted(async () => {
  document.body.classList.add("overflow-hidden");
  window.addEventListener("keydown", checkForEscapePress);

  if (props.name) {
    sidebarValue.value = props.name;

    router.push({
      query: {
        ...route.query,
        ...{ sidebar: props.name, ...(props.modelId && { id: props.modelId }) },
      },
    });
  } else if (props.ownsSidebar && !props.sidebarName) {
    // Legacy path: drawer opened via sidebar system without explicit sidebarName.
    // Parse from URL.
    if (route.query?.sidebar) {
      const sidebarParam = route.query.sidebar;

      if (typeof sidebarParam === "string" && sidebarParam.includes(",")) {
        const parts = sidebarParam.split(",");

        sidebarValue.value = parts[parts.length - 1];
      } else {
        sidebarValue.value = sidebarParam;
      }
    }
  }

  // Set a timer to prevent the drawer from opening too quickly and the animation won't work
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ANIMATION_DELAY_TIME_IN_MS);
  });

  isOpen.value = true;
});

// fetch the user information when params change
watch(
  () => route.params,
  () => {
    const { noClose = false } = route.query || {};

    if (props.closeOnRouteChange && !noClose) {
      close();
    }
  },
);

// Close drawer when it's removed from the sidebar stack
watch(
  () => route.query.sidebar,
  (newSidebar) => {
    if (!sidebarValue.value) {
      return;
    }

    // Parse the new stack and check if this drawer is still present
    const newSidebarStr = typeof newSidebar === "string" ? newSidebar : "";
    const sidebarNames = newSidebarStr.split(",").filter(Boolean);
    const stillInStack = sidebarNames.includes(sidebarValue.value);

    if (!stillInStack) {
      close();
    }
  },
);
</script>

<style lang="scss">
.drawer {
  &__content {
    @apply tw-fixed tw-top-0 tw-h-screen tw-w-11/12 tw-bg-white xl:tw-w-9/12;
    right: 0;
    transform: translateX(100%);
    transition:
      transform v-bind(CLOSE_ANIMATION_DURATION_IN_MS) ease,
      right v-bind(CLOSE_ANIMATION_DURATION_IN_MS) ease;
  }

  &__header {
    @apply tw-sticky tw-top-0 tw-flex tw-h-[64px] tw-items-center tw-justify-between tw-border-b tw-border-grey-100 tw-bg-white tw-p-6;
    z-index: 1;
  }

  // 40x40 hit area around a 20px xmark; the glyph itself is narrower than it is
  // tall, so the box is what makes the button square.
  &__close {
    @apply tw-h-[40px] tw-w-[40px] tw-min-w-[40px] tw-p-0;

    .q-icon {
      font-size: 20px;
    }
  }

  &__backdrop {
    @apply tw-pointer-events-none tw-fixed tw-inset-0 tw-cursor-pointer tw-bg-black tw-opacity-0;
    transition: opacity v-bind(CLOSE_ANIMATION_DURATION_IN_MS) ease;
  }

  &__body {
    @apply tw-flex tw-min-h-0 tw-flex-col;
    max-height: calc(100vh - v-bind(HEADER_HEIGHT_PX));
  }

  &__slot {
    @apply tw-flex-shrink tw-overflow-y-auto tw-pb-24;
  }

  &__actions {
    @apply tw-flex-shrink-0 tw-space-y-1 tw-border-t tw-border-grey-100 tw-bg-white tw-p-4;
    padding-bottom: calc(2 * v-bind(HEADER_HEIGHT_PX));
  }

  &.is-active & {
    &__content {
      transform: translateX(0);
    }

    &__backdrop {
      pointer-events: auto;
      opacity: 0.5;
    }
  }
}
</style>
